import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from '@/shared/hooks/use-toast';
import { whenFormIsClear, sameBuild, RELOAD_GUARD_KEY, RELOAD_GUARD_MS } from '@/lib/pwaUpdate';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// FIX-TV-PRELOGIN-A I1 (TVB-10): a backgrounded PWA never navigates, so the browser never re-checks sw.js on its
// own; ask for an update check when the app comes back into view, at most this often.
const UPDATE_CHECK_MIN_INTERVAL_MS = 30 * 60 * 1000;

/** FIX-TV-PRELOGIN-A2: ask a worker which build it is (sw.js answers GET_BUILD with its SW_BUILD stamp). A worker
 *  that does not answer within 3 s (an older sw.js without the handler) reports null. */
export function workerBuild(w: ServiceWorker, timeoutMs = 3000): Promise<string | null> {
  return new Promise((resolve) => {
    let done = false;
    const ch = new MessageChannel();
    const t = setTimeout(() => { if (!done) { done = true; resolve(null); } }, timeoutMs);
    ch.port1.onmessage = (e: MessageEvent) => { if (done) return; done = true; clearTimeout(t); resolve(typeof e.data?.build === 'string' ? e.data.build : null); };
    try { w.postMessage({ type: 'GET_BUILD' }, [ch.port2]); } catch { done = true; clearTimeout(t); resolve(null); }
  });
}

export const usePWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const lastUpdateCheck = useRef(0);

  // The banner's button: always immediate (the farmer asked).
  const applyUpdate = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [waitingWorker]);

  // FIX-TV-PRELOGIN-A I1 + A2: the forced update. The waiting worker takes over as soon as no form field has input —
  // but ONLY when it is THIS page's own build (GET_BUILD answer = the build stamped into this page). A worker of
  // another build (a stale sw.js from an edge cache, or one the browser fetched before this page's deploy) is never
  // auto-applied: it would delete this build's cache on activate and could ping-pong with this one — the banner
  // stays as the manual way. While a field has input the swap waits for a safe moment (focus leaves a field, a form
  // is submitted, the app is opened again). The reload itself happens on controllerchange below.
  useEffect(() => {
    if (!waitingWorker) return;
    let cancel: (() => void) | null = null;
    let gone = false;
    workerBuild(waitingWorker).then((build) => {
      if (gone) return;
      if (!sameBuild(build, __APP_VERSION__.commit)) {
        console.warn(`[PWA] waiting worker build ${build ?? 'unknown'} is not this page's build ${__APP_VERSION__.commit} — not applied automatically`);
        return;
      }
      cancel = whenFormIsClear(() => {
        console.log('[PWA] applying the new version now (no form input)');
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      });
    });
    return () => { gone = true; if (cancel) cancel(); };
  }, [waitingWorker]);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      toast({
        title: 'App Installed!',
        description: 'You can now use the app offline',
      });
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    let cancelReload: (() => void) | null = null;
    let onVisible: (() => void) | null = null;

    // Register service worker with update detection
    if ('serviceWorker' in navigator) {
      // true when a worker already controls this page: a later controllerchange is an UPDATE (reload once); on the
      // very first install (no controller yet) the page already runs the right build and must not reload mid-typing.
      const hadController = !!navigator.serviceWorker.controller;

      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);

          // Check for updates on registration
          registration.onupdatefound = () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            newWorker.onstatechange = () => {
              if (newWorker.state === 'installed') {
                // There's already a controller — this is an update, not first install
                if (navigator.serviceWorker.controller) {
                  console.log('[PWA] New version available');
                  setWaitingWorker(newWorker);
                  setUpdateAvailable(true);
                }
              }
            };
          };

          // Also check if there's already a waiting worker (e.g. from a previous visit)
          if (registration.waiting && navigator.serviceWorker.controller) {
            setWaitingWorker(registration.waiting);
            setUpdateAvailable(true);
          }

          // FIX-TV-PRELOGIN-A I1: the next open of a backgrounded app asks the browser for a new sw.js.
          onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            const now = Date.now();
            if (now - lastUpdateCheck.current < UPDATE_CHECK_MIN_INTERVAL_MS) return;
            lastUpdateCheck.current = now;
            registration.update().catch(() => undefined);
          };
          document.addEventListener('visibilitychange', onVisible);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });

      // When a NEW worker takes over an already-controlled page, reload once to get the fresh assets — never while
      // a form field has input (the swap may have come from another tab), and never twice within RELOAD_GUARD_MS
      // in this tab (A2: a reload storm can only be a worker ping-pong; one reload per minute at most).
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        if (!hadController) {
          console.log('[PWA] first install: no reload');
          return;
        }
        let lastReload = 0;
        try { lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0); } catch { /* storage unavailable */ }
        if (Date.now() - lastReload < RELOAD_GUARD_MS) {
          console.warn('[PWA] controllerchange again within the reload guard — not reloading');
          return;
        }
        refreshing = true;
        cancelReload = whenFormIsClear(() => {
          try { sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now())); } catch { /* storage unavailable */ }
          console.log('[PWA] reloading once for the new version');
          window.location.reload();
        });
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      if (cancelReload) cancelReload();
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstallable(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error prompting install:', error);
      return false;
    }
  };

  return {
    isInstallable,
    isInstalled,
    promptInstall,
    updateAvailable,
    applyUpdate,
  };
};
